import { useEffect, useState } from "react";
import { CredentiaIcon } from "../components/CredentiaLogo";

// ── Brand tokens ──────────────────────────────────────────────────────────────
const C = {
  navy:       "#0F1F2E",
  navyDark:   "#07101A",
  navyMid:    "#162B3D",
  teal:       "#2FAF9F",
  tealDark:   "#239187",
  grey:       "#F4F6F8",
  greyBorder: "#E2E8F0",
  white:      "#FFFFFF",
  textMuted:  "#64748B",
};

// ── Global CSS ────────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

  .lp *, .lp *::before, .lp *::after { box-sizing: border-box; }

  .lp {
    font-family: 'Inter', 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #0F1F2E;
    overflow-x: hidden;
  }

  .lp h1, .lp h2, .lp h3 {
    font-family: Georgia, 'Times New Roman', serif;
    font-weight: 700;
    line-height: 1.12;
    margin: 0;
  }

  .lp p { margin: 0; }

  /* ── Scroll animations ── */
  .fade-up {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1);
  }
  .fade-up.vis { opacity: 1; transform: translateY(0); }
  .fade-up.d1  { transition-delay: 0.08s; }
  .fade-up.d2  { transition-delay: 0.16s; }
  .fade-up.d3  { transition-delay: 0.24s; }

  /* ── Sticky nav ── */
  .lp-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    padding: 20px 60px;
    display: flex; align-items: center; justify-content: space-between;
    gap: 16px;
    transition: background 0.3s, box-shadow 0.3s, padding 0.3s;
  }
  .lp-nav.scrolled {
    background: rgba(7,16,26,0.96);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    box-shadow: 0 1px 0 rgba(255,255,255,0.06);
    padding: 13px 60px;
  }
  .lp-nav-brand { flex-shrink: 0; min-width: 0; }
  .lp-nav-brand-text { white-space: nowrap; }
  .lp-nav-links { display: flex; align-items: center; gap: 24px; flex-shrink: 0; }
  .lp-nav .nl { white-space: nowrap; }
  .lp-nav-cta { flex-shrink: 0; white-space: nowrap; }
  .lp-cta-short { display: none; }
  .lp-nav .nl {
    color: rgba(255,255,255,0.65); text-decoration: none;
    font-size: 0.9rem; font-weight: 500;
    transition: color 0.2s;
  }
  .lp-nav .nl:hover { color: #fff; }
  .lp-nav .nl-btn {
    color: rgba(255,255,255,0.78); text-decoration: none;
    font-size: 0.88rem; font-weight: 500;
    border: 1.5px solid rgba(255,255,255,0.22);
    padding: 7px 16px; border-radius: 7px;
    transition: color 0.2s, border-color 0.2s, background 0.2s;
  }
  .lp-nav .nl-btn:hover { color: #fff; border-color: rgba(255,255,255,0.5); background: rgba(255,255,255,0.05); }

  /* ── Buttons ── */
  .btn-teal {
    background: #2FAF9F; color: #fff; border: none; border-radius: 9px;
    padding: 14px 30px; font-weight: 600; font-size: 1rem; cursor: pointer;
    text-decoration: none; display: inline-block; line-height: 1.5;
    font-family: 'Inter', sans-serif;
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  }
  .btn-teal:hover { background: #239187; transform: translateY(-1px); box-shadow: 0 10px 28px rgba(47,175,159,0.38); }

  .btn-ghost-white {
    background: transparent; color: #fff; border-radius: 9px;
    border: 2px solid rgba(255,255,255,0.26);
    padding: 12px 26px; font-weight: 600; font-size: 1rem; cursor: pointer;
    text-decoration: none; display: inline-block; line-height: 1.5;
    font-family: 'Inter', sans-serif;
    transition: border-color 0.2s, background 0.2s, transform 0.15s;
  }
  .btn-ghost-white:hover { border-color: rgba(255,255,255,0.6); background: rgba(255,255,255,0.06); transform: translateY(-1px); }

  /* ── Cards ── */
  .feature-card {
    background: #fff;
    border: 1.5px solid #E8EDF2; border-radius: 16px; padding: 32px 28px;
    transition: box-shadow 0.25s, transform 0.25s, border-color 0.25s;
  }
  .feature-card:hover { box-shadow: 0 10px 36px rgba(15,31,46,0.08); transform: translateY(-3px); border-color: #CBD5E1; }

  .cesr-card {
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.09);
    border-radius: 14px; padding: 28px 24px;
  }

  /* ── Footer links ── */
  .lp footer a { color: rgba(255,255,255,0.45); text-decoration: none; font-size: 0.875rem; transition: color 0.2s; }
  .lp footer a:hover { color: rgba(255,255,255,0.85); }

  /* ── Responsive ── */
  @media (max-width: 900px) {
    .lp-nav         { padding: 16px 24px; }
    .lp-nav.scrolled{ padding: 12px 24px; }
    .lp-nav-links   { gap: 16px; }
    .hero-inner     { flex-direction: column; padding: 120px 24px 72px; gap: 48px; }
    .hero-mockup-w  { display: none; }
    .hero-h1        { font-size: 2.4rem !important; }
    .hero-sub       { font-size: 1rem !important; }
    .hero-ctas      { flex-direction: column; align-items: flex-start; }
    .cards-3        { grid-template-columns: 1fr !important; }
    .cesr-grid      { grid-template-columns: 1fr !important; }
  }

  /* Nav: two rows on narrow screens so logo and CTAs are not squashed */
  @media (max-width: 840px) {
    .lp-nav {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
      row-gap: 12px;
      align-items: center;
    }
    .lp-nav-brand { grid-row: 1; justify-self: start; }
    .lp-nav-links {
      grid-row: 2;
      width: 100%;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: nowrap;
    }
    .lp-nav .nl { font-size: 0.84rem; }
    .lp-nav-cta {
      font-size: 0.8rem !important;
      padding: 9px 14px !important;
    }
    .lp-cta-long { display: none !important; }
    .lp-cta-short { display: inline !important; }
  }

  @media (max-width: 400px) {
    .lp-nav-brand-text { display: none; }
    .lp-nav-brand img { width: 30px !important; height: 30px !important; }
  }

  @media (max-width: 360px) {
    .lp-nav-links { flex-direction: column; align-items: stretch; gap: 10px; }
    .lp-nav .nl { text-align: center; padding: 8px 0; border-radius: 8px; }
    .lp-nav-cta { text-align: center; white-space: normal; line-height: 1.35; }
  }

  @media (max-width: 480px) {
    .hero-h1        { font-size: 2rem !important; }
    .lp-nav         { padding: 14px 18px; }
    .lp-nav.scrolled{ padding: 10px 18px; }
  }
`;

// ── Smooth scroll helper ──────────────────────────────────────────────────────
const scrollTo = (id) => (e) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};


// ── CV Analyser mockup ────────────────────────────────────────────────────────
function CvMockup() {
  const domains = [
    { label: "Clinical Practice",   score: "Strong", color: "#2FAF9F" },
    { label: "Academic Activities", score: "Good",   color: "#2FAF9F" },
    { label: "Teaching & Training", score: "Strong", color: "#2FAF9F" },
    { label: "Audit & QI",          score: "Gaps",   color: "#F59E0B" },
    { label: "Safeguarding",        score: "Missing", color: "#EF4444" },
  ];
  return (
    <div style={{
      background: "#0C1B27", borderRadius: 16, overflow: "hidden",
      width: "100%", maxWidth: 500,
      boxShadow: "0 48px 100px rgba(0,0,0,0.65)",
      border: "1px solid rgba(255,255,255,0.07)",
      fontFamily: "Inter,sans-serif",
    }}>
      {/* Chrome */}
      <div style={{ background: "#08111A", padding: "11px 16px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        {["#FF5F57","#FEBC2E","#28C840"].map((c,i) => <div key={i} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
        <div style={{ flex: 1, textAlign: "center", color: "rgba(255,255,255,0.28)", fontSize: 11 }}>Credentia — CV Analyser</div>
      </div>
      <div style={{ padding: "20px 20px" }}>
        {/* Upload area */}
        <div style={{ background: "rgba(47,175,159,0.06)", border: "1.5px dashed rgba(47,175,159,0.3)", borderRadius: 10, padding: "14px 16px", marginBottom: 18, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 22 }}>📄</div>
          <div>
            <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 12, fontWeight: 600 }}>Dr_Smith_CV_2024.pdf</div>
            <div style={{ color: "#2FAF9F", fontSize: 10.5, marginTop: 2 }}>✓ Analysed against CESR criteria</div>
          </div>
        </div>
        {/* Domain scores */}
        <div style={{ color: "rgba(255,255,255,0.38)", fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>GMC Domain Coverage</div>
        {domains.map((d,i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.62)" }}>{d.label}</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: d.color, background: `${d.color}18`, padding: "2px 8px", borderRadius: 4 }}>{d.score}</span>
          </div>
        ))}
        {/* Verdict */}
        <div style={{ marginTop: 16, background: "rgba(47,175,159,0.08)", border: "1px solid rgba(47,175,159,0.2)", borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ color: "rgba(255,255,255,0.38)", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>AI Verdict</div>
          <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 11.5, lineHeight: 1.6 }}>
            Strong clinical and teaching profile. Safeguarding and Audit evidence gaps need addressing before submission.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Nav ───────────────────────────────────────────────────────────────────────
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
      <a href="/" className="lp-nav-brand" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
        <CredentiaIcon size={32} />
        <span className="lp-nav-brand-text" style={{ color: C.white, fontWeight: 700, fontSize: "1rem", letterSpacing: "-0.01em" }}>Credentia</span>
      </a>
      <div className="lp-nav-links">
        <a href="#cesr" className="nl" onClick={scrollTo("cesr")}>How it works</a>
        <a href="/cv" className="btn-teal lp-nav-cta" style={{ fontSize: "0.88rem", padding: "8px 18px" }}>
          <span className="lp-cta-long">Try free CV Analyser</span>
          <span className="lp-cta-short">Free CV tool</span>
        </a>
      </div>
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section style={{ background: C.navy, minHeight: "100vh", display: "flex", alignItems: "center" }}>
      <div className="hero-inner" style={{ maxWidth: 1180, margin: "0 auto", width: "100%", display: "flex", alignItems: "center", gap: 80, padding: "140px 60px 100px" }}>
        <div style={{ flex: "0 0 52%", minWidth: 0 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(47,175,159,0.1)", border: "1px solid rgba(47,175,159,0.22)", borderRadius: 100, padding: "5px 14px", marginBottom: 28 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.teal, boxShadow: `0 0 8px ${C.teal}` }} />
            <span style={{ color: C.teal, fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.05em" }}>Free CV Analyser — no account needed</span>
          </div>

          <h1 className="hero-h1" style={{ fontSize: "3.6rem", color: C.white, lineHeight: 1.08, marginBottom: 20 }}>
            Know exactly where<br />your CESR stands
          </h1>

          <p className="hero-sub" style={{ fontSize: "1.12rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.78, marginBottom: 38, maxWidth: 460 }}>
            Upload your CV and get an instant AI analysis of your GMC domain coverage, evidence gaps, and readiness for CESR submission — in under a minute, completely free.
          </p>

          <div className="hero-ctas" style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", marginBottom: 24 }}>
            <a href="/cv" className="btn-teal" style={{ fontSize: "1.05rem", padding: "15px 32px" }}>Analyse my CV — free</a>
            <a href="#cesr" className="btn-ghost-white" style={{ fontSize: "1rem", padding: "13px 26px" }} onClick={scrollTo("cesr")}>How it works →</a>
          </div>

          <p style={{ color: "rgba(255,255,255,0.32)", fontSize: "0.85rem" }}>
            No account required for the CV analyser &nbsp;·&nbsp; Takes under 60 seconds
          </p>
        </div>

        <div className="hero-mockup-w" style={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "flex-end" }}>
          <CvMockup />
        </div>
      </div>
    </section>
  );
}

// ── CV Analyser section ───────────────────────────────────────────────────────
function CvSection() {
  const cards = [
    {
      icon: "🔍",
      title: "Instant CESR gap analysis",
      body: "Our AI reads your CV against the GMC's 14-domain framework and tells you exactly what's strong, what needs work, and what's missing entirely.",
    },
    {
      icon: "📋",
      title: "Domain-by-domain verdict",
      body: "Get a structured breakdown of how your experience maps to Clinical Practice, Teaching, Audit, Safeguarding, and every other domain the GMC will assess.",
    },
    {
      icon: "🎯",
      title: "Actionable next steps",
      body: "Not just a score — a clear list of the specific evidence types you still need, so you can plan your remaining training and submissions with confidence.",
    },
  ];

  return (
    <section style={{ background: C.white, padding: "96px 60px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div className="fade-up" style={{ textAlign: "center", marginBottom: 56 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(47,175,159,0.08)", border: "1px solid rgba(47,175,159,0.2)", borderRadius: 100, padding: "5px 14px", marginBottom: 20 }}>
            <span style={{ color: C.teal, fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.05em" }}>Free tool</span>
          </div>
          <h2 style={{ fontSize: "2.4rem", color: C.navy, marginBottom: 14 }}>
            Understand your CESR readiness<br />before you start gathering evidence
          </h2>
          <p style={{ color: C.textMuted, fontSize: "1.05rem", maxWidth: 560, margin: "0 auto" }}>
            Most doctors don't know which domains they're weak on until they're deep into the process. Our CV analyser gives you that picture in seconds.
          </p>
        </div>

        <div className="cards-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, marginBottom: 52 }}>
          {cards.map((c, i) => (
            <div key={i} className={`feature-card fade-up d${i + 1}`}>
              <div style={{ fontSize: 32, marginBottom: 18 }}>{c.icon}</div>
              <h3 style={{ fontSize: "1.1rem", color: C.navy, marginBottom: 12, fontFamily: "Inter,sans-serif", fontWeight: 700 }}>{c.title}</h3>
              <p style={{ color: C.textMuted, lineHeight: 1.72, fontSize: "0.93rem" }}>{c.body}</p>
            </div>
          ))}
        </div>

        <div className="fade-up" style={{ textAlign: "center" }}>
          <a href="/cv" className="btn-teal" style={{ fontSize: "1.05rem", padding: "15px 36px" }}>
            Try the free CV analyser
          </a>
          <p style={{ color: C.textMuted, fontSize: "0.85rem", marginTop: 14 }}>
            No account. No credit card. Just upload your CV.
          </p>
        </div>
      </div>
    </section>
  );
}

// ── CESR pathway section ──────────────────────────────────────────────────────
function CesrSection() {
  const points = [
    {
      icon: "📂",
      title: "18 months of evidence, scattered everywhere",
      body: "Certificates in emails, ePortfolio entries you can't export, supervisor forms still outstanding. Credentia gives every piece of evidence a home — organised by GMC domain, correctly named, ready to submit.",
    },
    {
      icon: "⏰",
      title: "ALS expiring. Appraisal overdue. Safeguarding lapsed.",
      body: "Credentia tracks every expiry date and sends you reminders before they become a problem. Reach submission with everything current — not scrambling to recertify at the last minute.",
    },
    {
      icon: "✅",
      title: "Know when you're actually ready",
      body: "Our readiness score doesn't just count documents — it weights mandatory vs optional evidence, flags out-of-date clinical records, and tells you honestly when you're submission-ready. No surprises at the GMC.",
    },
  ];

  return (
    <section id="cesr" style={{ background: C.navy, padding: "100px 60px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div className="fade-up" style={{ textAlign: "center", marginBottom: 64 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(47,175,159,0.1)", border: "1px solid rgba(47,175,159,0.22)", borderRadius: 100, padding: "5px 14px", marginBottom: 20 }}>
            <span style={{ color: C.teal, fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.05em" }}>Coming soon</span>
          </div>
          <h2 style={{ fontSize: "2.4rem", color: C.white, marginBottom: 16 }}>
            Making the CESR pathway<br />manageable
          </h2>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "1.05rem", maxWidth: 540, margin: "0 auto" }}>
            We're building a full portfolio tracker that takes you from scattered evidence to GMC-ready submission — specialty by specialty, domain by domain.
          </p>
        </div>

        <div className="cesr-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 64 }}>
          {points.map((p, i) => (
            <div key={i} className={`cesr-card fade-up d${i + 1}`}>
              <div style={{ fontSize: 28, marginBottom: 16 }}>{p.icon}</div>
              <h3 style={{ fontSize: "1.05rem", color: C.white, marginBottom: 12, fontFamily: "Inter,sans-serif", fontWeight: 700, lineHeight: 1.4 }}>{p.title}</h3>
              <p style={{ color: "rgba(255,255,255,0.52)", lineHeight: 1.72, fontSize: "0.92rem" }}>{p.body}</p>
            </div>
          ))}
        </div>

        <div className="fade-up" style={{ textAlign: "center" }}>
          <a href="mailto:hello@credentia.pro" className="btn-teal" style={{ fontSize: "1.05rem", padding: "15px 34px" }}>
            Register your interest
          </a>
          <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.82rem", marginTop: 16 }}>
            Currently available for Anaesthetics &amp; Emergency Medicine &nbsp;·&nbsp; Invite only
          </p>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer style={{ background: C.navyDark, padding: "44px 60px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 24, marginBottom: 28 }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <CredentiaIcon size={28} />
            <span style={{ color: C.white, fontWeight: 700, fontSize: "0.97rem" }}>Credentia</span>
          </a>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <a href="/privacy.html">Privacy Policy</a>
            <a href="/terms.html">Terms</a>
            <a href="mailto:hello@credentia.pro">Contact</a>
            <a href="https://medberry.zendesk.com" target="_blank" rel="noopener noreferrer">Support</a>
          </div>
        </div>
        <div style={{ height: 1, background: "rgba(255,255,255,0.05)", marginBottom: 22 }} />
        <p style={{ color: "rgba(255,255,255,0.26)", fontSize: "0.8rem", lineHeight: 1.6 }}>
          © 2026 Medberry Ltd. Credentia is not affiliated with the GMC or Royal Colleges. Always check current GMC CESR guidance.
        </p>
      </div>
    </footer>
  );
}

// ── Landing (main export) ─────────────────────────────────────────────────────
export default function Landing() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("vis"); }),
      { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
    );
    document.querySelectorAll(".fade-up").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="lp">
      <style>{GLOBAL_CSS}</style>
      <Nav />
      <Hero />
      <CvSection />
      <CesrSection />
      <Footer />
    </div>
  );
}
